<template>
  <div v-if="reportStore.isLoading" class="flex justify-center items-center h-64">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>

  <div v-else-if="reportStore.currentReport" ref="reportContainer" id="report-content" class="max-w-7xl mx-auto">
    <!-- Header -->
    <div class="bg-white shadow-xl rounded-xl mb-8 border border-gray-100">
      <div class="px-8 py-6">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold text-gray-900 tracking-tight leading-tight">
              Informe Diagnóstico de<br>Laboratorio v3.0
            </h1>
            <p class="text-gray-600 text-sm mt-2 font-medium">
              Sistema de Análisis Clínico • powered by SABIODOC ®
            </p>
          </div>
          <div class="flex space-x-4">
            <!-- Primary CTA - Informe Paciente -->
            <button
              @click="showPatientExplanation"
              class="px-5 py-3 bg-emerald-500 text-white rounded-md hover:bg-emerald-600 transition-all duration-200 font-medium text-sm capitalize tracking-wide shadow-md hover:shadow-lg"
            >
              <svg class="w-4 h-4 inline mr-1" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              Informe paciente
            </button>
            
            <!-- Secondary CTAs -->
            <button
              @click="previewPDF"
              :disabled="isExporting || isPreviewing"
              class="px-4 py-3 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 transition-all duration-200 font-medium text-sm capitalize tracking-wide shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="!isPreviewing">👁 Vista previa PDF</span>
              <span v-else class="flex items-center">
                <span class="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                Abriendo vista previa...
              </span>
            </button>
            
            <button
              @click="exportToPDF"
              :disabled="isExporting"
              class="px-4 py-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-all duration-200 font-medium text-sm capitalize tracking-wide shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div v-if="!isExporting" class="flex items-center">
                ⬇ Exportar PDF
              </div>
              <div v-else class="flex items-center">
                <div class="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                Exportando...
              </div>
            </button>
            
            <!-- Tertiary CTA - Navigation -->
            <button
              @click="goBack"
              class="px-4 py-3 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-all duration-200 font-medium text-sm capitalize shadow-sm hover:shadow-md"
            >
              ◀ Volver
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 1. Información del Paciente -->
    <div class="bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg rounded-xl mb-8 border border-blue-100">
      <div class="px-8 py-6">
        <h2 class="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <span class="text-blue-600 mr-3 text-2xl">👤</span>
          Información del Paciente
        </h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div class="bg-white/70 backdrop-blur-sm rounded-lg p-4 shadow-sm border border-white/50">
            <p class="text-sm text-gray-600 font-medium mb-1">Nombre</p>
            <p class="font-bold text-gray-900 text-lg">{{ reportStore.currentReport.patientName || 'No especificado' }}</p>
          </div>
          <div class="bg-white/70 backdrop-blur-sm rounded-lg p-4 shadow-sm border border-white/50">
            <p class="text-sm text-gray-600 font-medium mb-1">Edad</p>
            <p class="font-bold text-gray-900 text-lg">{{ reportStore.currentReport.patientAge || 'No especificada' }} años</p>
          </div>
          <div class="bg-white/70 backdrop-blur-sm rounded-lg p-4 shadow-sm border border-white/50">
            <p class="text-sm text-gray-600 font-medium mb-1">Sexo</p>
            <p class="font-bold text-gray-900 text-lg">{{ reportStore.currentReport.patientSex || 'No especificado' }}</p>
          </div>
          <div class="bg-white/70 backdrop-blur-sm rounded-lg p-4 shadow-sm border border-white/50">
            <p class="text-sm text-gray-600 font-medium mb-1">Fecha de Muestra</p>
            <p class="font-bold text-gray-900 text-lg">{{ reportStore.currentReport.sampleDate }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. Resultados de Laboratorio -->
    <div v-if="reportStore.currentReport.labResults" class="bg-green-50 shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span class="text-green-500 mr-2">🧪</span>
          Resultados de Laboratorio
        </h2>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parámetro</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unidad</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rango Referencia</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(result, index) in reportStore.currentReport.labResults.resultados" :key="index">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ result.parametro }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ result.valor ?? 'N/A' }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ result.unidad }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ result.rango_referencia }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 3. Validación Analítica -->
    <div v-if="reportStore.currentReport.clinicalAnalysis?.validation" class="bg-yellow-50 shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span class="text-yellow-500 mr-2">✅</span>
          Validación Analítica
        </h2>
        <div class="bg-white p-4 rounded-lg border-l-4" :class="getStatusColor(reportStore.currentReport.clinicalAnalysis.validation.analytical_consistency.status)">
          <div class="flex items-center justify-between mb-2">
            <div>
              <div class="font-semibold text-lg">{{ getStatusLabel(reportStore.currentReport.clinicalAnalysis.validation.analytical_consistency.status) }}</div>
              <div class="text-sm text-gray-600">Estado de Consistencia Analítica</div>
            </div>
            <div class="text-3xl font-bold">
              {{ reportStore.currentReport.clinicalAnalysis.validation.analytical_consistency.confidence_score }}%
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 4. Interpretación Completa -->
    <div v-if="reportStore.currentReport.clinicalAnalysis?.interpretation" class="bg-blue-50 shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span class="text-blue-500 mr-2">🔍</span>
          Interpretación Clínica Completa
        </h2>
        <div class="bg-blue-100 p-4 rounded-lg">
          <!-- <div class="text-sm text-blue-800 text-justify leading-relaxed"> -->
          <div class="text-sm text-gray-800 text-justify leading-relaxed">
            {{ reportStore.currentReport.clinicalAnalysis.interpretation }}
          </div>
        </div>
      </div>
    </div>

    <!-- 5. Resumen Ejecutivo -->
    <div v-if="reportStore.currentReport.clinicalAnalysis?.summary_executive" class="bg-indigo-50 shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span class="text-indigo-500 mr-2">📋</span>
          Resumen Ejecutivo
        </h2>
        <div class="bg-indigo-100 p-4 rounded-lg">
          <!-- <div class="text-sm text-indigo-800 font-medium"> -->
          <div class="text-sm text-gray-800 font-medium">
            {{ reportStore.currentReport.clinicalAnalysis.summary_executive }}
          </div>
        </div>
      </div>
    </div>

    <!-- 6. Análisis Mecanístico -->
    <div v-if="reportStore.currentReport.clinicalAnalysis?.mechanistic_analysis" class="bg-purple-50 shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span class="text-purple-500 mr-2">⚙️</span>
          Análisis Mecanístico
        </h2>
        <div class="space-y-4">
          <div v-if="reportStore.currentReport.clinicalAnalysis.mechanistic_analysis.primary_mechanism" class="bg-purple-100 p-4 rounded-lg">
            <!-- <h3 class="font-semibold text-purple-800 mb-2">Mecanismo Primario:</h3> -->
            <h3 class="font-semibold text-gray-800 mb-2">Mecanismo Primario:</h3>
            <!-- <div class="text-sm text-purple-700 text-justify leading-relaxed"> -->
            <div class="text-sm text-gray-700 text-justify leading-relaxed">
              {{ reportStore.currentReport.clinicalAnalysis.mechanistic_analysis.primary_mechanism }}
            </div>
          </div>
          <div v-if="reportStore.currentReport.clinicalAnalysis.mechanistic_analysis.evidence_basis" class="bg-purple-100 p-4 rounded-lg">
            <!-- <h3 class="font-semibold text-purple-800 mb-2">Base de Evidencia:</h3> -->
            <h3 class="font-semibold text-gray-800 mb-2">Base de Evidencia:</h3>
            <!-- <div class="text-sm text-purple-700 text-justify leading-relaxed"> -->
            <div class="text-sm text-gray-700 text-justify leading-relaxed">
              {{ reportStore.currentReport.clinicalAnalysis.mechanistic_analysis.evidence_basis }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 7. Banderas Rojas Críticas -->
    <div v-if="reportStore.currentReport.clinicalAnalysis?.red_flags && reportStore.currentReport.clinicalAnalysis.red_flags.length > 0" class="bg-red-50 shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span class="text-red-500 mr-2">🚩</span>
          Banderas Rojas Críticas
        </h2>
        <div class="space-y-3">
          <div v-for="(flag, index) in reportStore.currentReport.clinicalAnalysis.red_flags" :key="index" 
               class="bg-white p-4 rounded-lg border-l-4" :class="getSeverityBorder(flag.severity)">
            <div class="flex items-start justify-between mb-2">
              <div>
                <!-- <div class="font-semibold text-red-800">{{ flag.parameter }}</div> -->
                <div class="font-semibold text-gray-800">{{ flag.parameter }}</div>
                <!-- <div class="text-xs text-red-600 mt-1"> -->
                <div class="text-xs text-gray-600 mt-1">
                  Severidad: <span class="font-medium uppercase">{{ flag.severity }}</span>
                </div>
              </div>
              <span class="px-3 py-1 rounded-full text-xs font-medium" :class="getSeverityBadge(flag.severity)">
                {{ flag.severity }}
              </span>
            </div>
            <div class="text-sm text-gray-700 mt-2">
              <strong>Impacto Clínico:</strong> {{ flag.clinical_impact }}
            </div>
            <div v-if="flag.urgent_actions && flag.urgent_actions.length > 0" class="mt-3">
              <!-- <div class="text-sm font-medium text-red-700 mb-1">Acciones Urgentes:</div> -->
              <div class="text-sm font-medium text-gray-700 mb-1">Acciones Urgentes:</div>
              <!-- <ul class="list-disc list-inside text-sm text-red-600 space-y-1"> -->
              <ul class="list-disc list-inside text-sm text-gray-700 space-y-1">
                <li v-for="(action, idx) in flag.urgent_actions" :key="idx">{{ action }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 8. Inconsistencias Analíticas -->
    <div v-if="reportStore.currentReport.clinicalAnalysis?.validation?.analytical_consistency?.inconsistencies && 
                reportStore.currentReport.clinicalAnalysis.validation.analytical_consistency.inconsistencies.length > 0" 
         class="bg-orange-50 shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span class="text-orange-500 mr-2">⚠️</span>
          Inconsistencias Analíticas
        </h2>
        <div class="space-y-3">
          <div v-for="(inconsistency, index) in reportStore.currentReport.clinicalAnalysis.validation.analytical_consistency.inconsistencies" 
               :key="index" class="bg-white p-4 rounded-lg border-l-4 border-orange-400">
            <div class="font-semibold text-orange-800 mb-2">
              {{ inconsistency.parameter1 }} ↔ {{ inconsistency.parameter2 }}
            </div>
            <div class="text-sm space-y-2">
              <div>
                <span class="font-medium text-gray-700">Relación Esperada:</span>
                <p class="text-gray-600">{{ inconsistency.expected_relationship }}</p>
              </div>
              <div>
                <span class="font-medium text-gray-700">Discrepancia Observada:</span>
                <p class="text-gray-600">{{ inconsistency.observed_discrepancy }}</p>
              </div>
              <div>
                <span class="font-medium text-gray-700">Significancia Clínica:</span>
                <p class="text-gray-600">{{ inconsistency.clinical_significance }}</p>
              </div>
              <div class="bg-orange-50 p-2 rounded mt-2">
                <span class="font-medium text-orange-700">Acción Correctiva:</span>
                <p class="text-orange-600 text-sm">{{ inconsistency.corrective_action }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 9. Pruebas Diagnósticas Recomendadas -->
    <div v-if="reportStore.currentReport.clinicalAnalysis?.clinical_guidance?.critical_tests && 
                reportStore.currentReport.clinicalAnalysis.clinical_guidance.critical_tests.length > 0" 
         class="bg-green-50 shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span class="text-green-500 mr-2">🔬</span>
          Pruebas Diagnósticas Recomendadas
        </h2>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prueba</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prioridad</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Justificación</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="(test, index) in reportStore.currentReport.clinicalAnalysis.clinical_guidance.critical_tests" :key="index">
                <td class="px-6 py-4 text-sm font-medium text-gray-900">{{ test.test }}</td>
                <td class="px-6 py-4 text-sm">
                  <span class="px-3 py-1 rounded-full text-xs font-medium" :class="getPriorityBadge(test.priority)">
                    {{ test.priority }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm text-gray-600">{{ test.rationale }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 10. Control de Calidad (Auditoría) -->
    <div v-if="reportStore.currentReport.auditResults" class="bg-gray-50 shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span class="text-gray-500 mr-2">🔍</span>
          Control de Calidad (Auditoría)
        </h2>
        
        <!-- Approval Status -->
        <div class="mb-4 p-4 rounded-lg" :class="reportStore.currentReport.auditResults.approved ? 'bg-green-100' : 'bg-red-100'">
          <div class="flex items-center">
            <span class="text-2xl mr-3">{{ reportStore.currentReport.auditResults.approved ? '✅' : '❌' }}</span>
            <div>
              <div class="font-semibold" :class="reportStore.currentReport.auditResults.approved ? 'text-green-800' : 'text-red-800'">
                {{ reportStore.currentReport.auditResults.approved ? 'Análisis Aprobado' : 'Análisis Rechazado' }}
              </div>
              <div class="text-sm text-gray-600">Estado de validación del análisis clínico</div>
            </div>
          </div>
        </div>

        <!-- Quality Scores -->
        <div class="grid grid-cols-3 gap-4 mb-4">
          <div class="bg-white p-4 rounded-lg text-center">
            <div class="text-3xl font-bold text-blue-600">{{ reportStore.currentReport.auditResults.quality_score.interpretation }}/10</div>
            <div class="text-sm text-gray-600 mt-1">Interpretación</div>
          </div>
          <div class="bg-white p-4 rounded-lg text-center">
            <div class="text-3xl font-bold text-green-600">{{ reportStore.currentReport.auditResults.quality_score.consistency }}/10</div>
            <div class="text-sm text-gray-600 mt-1">Consistencia</div>
          </div>
          <div class="bg-white p-4 rounded-lg text-center">
            <div class="text-3xl font-bold text-purple-600">{{ reportStore.currentReport.auditResults.quality_score.actionability }}/10</div>
            <div class="text-sm text-gray-600 mt-1">Accionabilidad</div>
          </div>
        </div>

        <!-- Feedback -->
        <div class="space-y-3">
          <div v-if="reportStore.currentReport.auditResults.feedback.strengths && reportStore.currentReport.auditResults.feedback.strengths.length > 0" 
               class="bg-green-50 p-4 rounded-lg">
            <h3 class="font-semibold text-green-800 mb-2">✅ Fortalezas</h3>
            <ul class="list-disc list-inside text-sm text-green-700 space-y-1">
              <li v-for="(strength, idx) in reportStore.currentReport.auditResults.feedback.strengths" :key="idx">{{ strength }}</li>
            </ul>
          </div>

          <div v-if="reportStore.currentReport.auditResults.feedback.improvements && reportStore.currentReport.auditResults.feedback.improvements.length > 0" 
               class="bg-yellow-50 p-4 rounded-lg">
            <h3 class="font-semibold text-yellow-800 mb-2">💡 Mejoras Sugeridas</h3>
            <ul class="list-disc list-inside text-sm text-yellow-700 space-y-1">
              <li v-for="(improvement, idx) in reportStore.currentReport.auditResults.feedback.improvements" :key="idx">{{ improvement }}</li>
            </ul>
          </div>

          <div v-if="reportStore.currentReport.auditResults.feedback.critical_issues && reportStore.currentReport.auditResults.feedback.critical_issues.length > 0" 
               class="bg-red-50 p-4 rounded-lg">
            <h3 class="font-semibold text-red-800 mb-2">🚨 Issues Críticos</h3>
            <ul class="list-disc list-inside text-sm text-red-700 space-y-1">
              <li v-for="(issue, idx) in reportStore.currentReport.auditResults.feedback.critical_issues" :key="idx">{{ issue }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- 11. Validación Final -->
    <div v-if="reportStore.currentReport.qualityValidation" class="bg-gradient-to-r from-blue-50 to-green-50 shadow rounded-lg mb-6">
      <div class="px-6 py-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span class="text-blue-500 mr-2">🏆</span>
          Validación Final del Sistema
        </h2>
        
        <div class="flex items-center justify-between mb-4 p-4 bg-white rounded-lg">
          <div>
            <div class="flex items-center">
              <span class="text-3xl mr-3">{{ reportStore.currentReport.qualityValidation.approved ? '✅' : '❌' }}</span>
              <div>
                <div class="font-bold text-xl" :class="reportStore.currentReport.qualityValidation.approved ? 'text-green-600' : 'text-red-600'">
                  {{ reportStore.currentReport.qualityValidation.approved ? 'Reporte Aprobado' : 'Reporte Rechazado' }}
                </div>
                <div class="text-sm text-gray-600">Validación del pipeline completo</div>
              </div>
            </div>
          </div>
          <div class="text-center">
            <div class="text-5xl font-bold" :class="getScoreColor(reportStore.currentReport.qualityValidation.final_score)">
              {{ reportStore.currentReport.qualityValidation.final_score }}
            </div>
            <div class="text-sm text-gray-600">Score Final</div>
          </div>
        </div>

        <div v-if="reportStore.currentReport.qualityValidation.validation_checks && reportStore.currentReport.qualityValidation.validation_checks.length > 0" 
             class="space-y-2">
          <h3 class="font-semibold text-gray-800 mb-2">Checks de Validación:</h3>
          <div v-for="(check, index) in reportStore.currentReport.qualityValidation.validation_checks" :key="index" 
               class="flex items-center justify-between p-3 bg-white rounded-lg">
            <span class="text-sm text-gray-700">{{ check.check }}</span>
            <span class="text-xl">{{ check.passed ? '✅' : '❌' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Explicación Paciente -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click.self="closeModal">
      <div class="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div class="sticky top-0 bg-blue-900 px-6 py-5 flex items-center justify-between rounded-t-sm border-b border-blue-800">
          <div>
            <h3 class="text-xl font-bold text-white tracking-wide">Informe para el Paciente</h3>
            <p class="text-blue-100 text-sm mt-1 font-light">Interpretación clínica en lenguaje comprensible</p>
          </div>
          <button @click="closeModal" class="text-white hover:text-gray-200 text-3xl font-light">&times;</button>
        </div>
        <div class="p-6 text-gray-800">
          <div v-if="reportStore.currentReport.patientReport" class="space-y-6">
            <!-- <p class="text-lg font-semibold text-green-800"> -->
            <p class="text-lg font-semibold text-gray-800">
              Hola {{ reportStore.currentReport.patientName || 'Paciente' }}, aquí tienes un resumen de tus resultados en un lenguaje sencillo:
            </p>

            <!-- Summary -->
            <div v-if="reportStore.currentReport.patientReport.summary" class="bg-blue-50 p-4 rounded-lg">
              <!-- <h5 class="font-medium text-blue-800 mb-2 flex items-center"> -->
              <h5 class="font-medium text-gray-800 mb-2 flex items-center">
                <span class="mr-2">📋</span> Resumen
              </h5>
              <!-- <p class="text-sm text-blue-700 text-justify leading-relaxed"> -->
              <p class="text-sm text-gray-700 text-justify leading-relaxed">
                {{ reportStore.currentReport.patientReport.summary }}
              </p>
            </div>

            <!-- Explanations -->
            <div v-if="reportStore.currentReport.patientReport.explanations && reportStore.currentReport.patientReport.explanations.length > 0" 
                 class="bg-purple-50 p-4 rounded-lg">
              <!-- <h5 class="font-medium text-purple-800 mb-3 flex items-center"> -->
              <h5 class="font-medium text-gray-800 mb-3 flex items-center">
                <span class="mr-2">💡</span> Explicaciones
              </h5>
              <div class="space-y-2">
                <div v-for="(explanation, index) in reportStore.currentReport.patientReport.explanations" :key="index" 
                     class="flex items-start">
                  <!-- <span class="text-purple-500 mr-2 mt-1">•</span> -->
                  <span class="text-gray-500 mr-2 mt-1">•</span>
                  <!-- <p class="text-sm text-purple-700 text-justify leading-relaxed flex-1">{{ explanation }}</p> -->
                  <p class="text-sm text-gray-700 text-justify leading-relaxed flex-1">{{ explanation }}</p>
                </div>
              </div>
            </div>

            <!-- Actions -->
            <div v-if="reportStore.currentReport.patientReport.actions && reportStore.currentReport.patientReport.actions.length > 0" 
                 class="bg-green-50 p-4 rounded-lg">
              <!-- <h5 class="font-medium text-green-800 mb-3 flex items-center"> -->
              <h5 class="font-medium text-gray-800 mb-3 flex items-center">
                <span class="mr-2">✅</span> Acciones Recomendadas
              </h5>
              <div class="space-y-2">
                <div v-for="(action, index) in reportStore.currentReport.patientReport.actions" :key="index" 
                     class="flex items-start bg-white p-3 rounded border-l-4 border-green-400">
                  <!-- <span class="text-green-600 font-bold mr-2">{{ index + 1 }}.</span> -->
                  <span class="text-gray-600 font-bold mr-2">{{ index + 1 }}.</span>
                  <!-- <p class="text-sm text-green-700 flex-1">{{ action }}</p> -->
                  <p class="text-sm text-gray-700 flex-1">{{ action }}</p>
                </div>
              </div>
            </div>

            <!-- FAQs -->
            <div v-if="reportStore.currentReport.patientReport.faqs && reportStore.currentReport.patientReport.faqs.length > 0" 
                 class="bg-yellow-50 p-4 rounded-lg">
              <!-- <h5 class="font-medium text-yellow-800 mb-3 flex items-center"> -->
              <h5 class="font-medium text-gray-800 mb-3 flex items-center">
                <span class="mr-2">❓</span> Preguntas Frecuentes
              </h5>
              <div class="space-y-3">
                <div v-for="(faq, index) in reportStore.currentReport.patientReport.faqs" :key="index" 
                     class="bg-white p-3 rounded">
                  <!-- <p class="text-sm text-yellow-700 leading-relaxed">{{ faq }}</p> -->
                  <p class="text-sm text-gray-700 leading-relaxed">{{ faq }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-8 text-gray-500">
            <span class="text-4xl">📋</span>
            <p class="mt-2">No hay reporte para paciente disponible</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Botón flotante para subir -->
  <button
    @click="scrollToTop"
    :class="[
      'fixed bottom-6 right-6 bg-blue-500 hover:bg-blue-600 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 z-50',
      showScrollButton ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-75 translate-y-2 pointer-events-none'
    ]"
    title="Subir al inicio"
  >
    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
    </svg>
  </button>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useReportStore } from '../stores/reportStore'
import { useAuthStore } from '../stores/auth'
import { checkAuthStatus, autoLogin } from '../utils/checkAuth'
import { useToast } from 'vue-toastification'
import { ReportService } from '../services/ReportService'

const route = useRoute()
const router = useRouter()
const reportStore = useReportStore()
const toast = useToast()

const isExporting = ref(false)
const isPreviewing = ref(false)
const showModal = ref(false)
const reportContainer = ref<HTMLElement | null>(null)
const showScrollButton = ref(false)

// Función para subir al inicio de la página
const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

// Función para manejar el scroll y mostrar/ocultar el botón
const handleScroll = () => {
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  showScrollButton.value = scrollTop > 300 // Mostrar después de 300px de scroll
}

onMounted(async () => {
  console.log('🔍 ResultsV2: Montando componente...')
  
  const isAuthenticated = await checkAuthStatus()
  console.log('🔐 Autenticación:', isAuthenticated)
  
  if (!isAuthenticated) {
    const autoLoginSuccess = await autoLogin()
    console.log('🔄 Auto-login:', autoLoginSuccess)
    if (!autoLoginSuccess) {
      toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.')
      router.push('/auth')
      return
    }
  }

  const reportId = route.params.reportId as string  // Fix: usar 'reportId' no 'id'
  console.log('📄 Report ID:', reportId)
  
  if (reportId) {
    try {
      // Fix: Usar getReport en lugar de fetchReportById (que no existe)
      console.log('📥 Cargando reporte...')
      await reportStore.getReport(reportId)
      console.log('✅ Reporte cargado:', reportStore.currentReport)
      
      if (!reportStore.currentReport) {
        console.error('❌ currentReport es null después de getReport')
        toast.error('No se pudo cargar el reporte')
      }
    } catch (error) {
      console.error('❌ Error cargando reporte:', error)
      toast.error('Error al cargar el reporte: ' + (error instanceof Error ? error.message : 'Error desconocido'))
    }
  }
  
  // Agregar event listener para el scroll
  window.addEventListener('scroll', handleScroll)
})

// Limpiar event listener cuando el componente se desmonte
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

const goBack = () => {
  router.push('/reports')
}

const showPatientExplanation = () => {
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
}

const exportToPDF = async () => {
  if (!reportStore.currentReport?.id) {
    toast.error('Reporte no cargado')
    return
  }
  isExporting.value = true
  try {
    const service = new ReportService()
    const blob = await service.downloadReportPDF(reportStore.currentReport.id)
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `reporte_${reportStore.currentReport.patientName || 'paciente'}_${new Date().toISOString().split('T')[0]}.pdf`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
    toast.success('Reporte exportado exitosamente')
  } catch (error) {
    console.error('Error exporting PDF:', error)
    toast.error('Error al exportar el reporte')
  } finally {
    isExporting.value = false
  }
}

const previewPDF = async () => {
  if (!reportStore.currentReport?.id) {
    toast.error('Reporte no cargado')
    return
  }
  try {
    isPreviewing.value = true
    
    // Crear nombre de archivo descriptivo (mismo formato que exportToPDF)
    const patientName = reportStore.currentReport.patientName || 'paciente'
    const sampleDate = reportStore.currentReport.sampleDate || new Date().toISOString().split('T')[0]
    const fileName = `reporte_${patientName}_${sampleDate}.pdf`
    
    // Usar directamente la URL del servidor para preservar el nombre del archivo
    // El Content-Disposition: inline del backend preservará el nombre
    const authStore = useAuthStore()
    const service = new ReportService()
    const pdfUrl = `${service.getBaseURL()}/api/reports/${reportStore.currentReport.id}/pdf`
    
    // Crear URL con token de autenticación como query parameter
    // Nota: Esto es menos seguro pero necesario para iframes
    const urlWithAuth = authStore.accessToken 
      ? `${pdfUrl}?token=${encodeURIComponent(authStore.accessToken)}`
      : pdfUrl
    
    // Abrir en nueva ventana y mostrar pantalla de carga primero
    const win = window.open('', '_blank')
    if (win) {
      // Construir HTML con pantalla de carga que se oculta cuando el PDF carga
      const htmlDoc = win.document
      htmlDoc.open()
      htmlDoc.write('<!DOCTYPE html>')
      htmlDoc.write('<html>')
      htmlDoc.write('<head>')
      htmlDoc.write('<title>' + fileName + '</title>')
      htmlDoc.write('<meta name="viewport" content="width=device-width, initial-scale=1" />')
      htmlDoc.write('<style>')
      htmlDoc.write('body{margin:0;display:flex;align-items:center;justify-content:center;height:100vh;background:#0ea5e9;color:#fff;font-family:system-ui,Segoe UI,Roboto,Helvetica,Arial,sans-serif}')
      htmlDoc.write('.card{background:rgba(255,255,255,.1);backdrop-filter:saturate(180%) blur(8px);padding:24px 28px;border-radius:12px;box-shadow:0 10px 30px rgba(0,0,0,.15);text-align:center}')
      htmlDoc.write('.spin{width:28px;height:28px;border:3px solid rgba(255,255,255,.6);border-top-color:#fff;border-radius:50%;animation:spin 1s linear infinite;display:inline-block;margin-right:10px}')
      htmlDoc.write('@keyframes spin{to{transform:rotate(360deg)}}')
      htmlDoc.write('.loader{display:flex;align-items:center;justify-content:center}')
      htmlDoc.write('.loader.hidden{display:none}')
      htmlDoc.write('#pdfFrame{display:none;border:0;width:100%;height:100%;position:absolute;top:0;left:0}')
      htmlDoc.write('#pdfFrame.loaded{display:block}')
      htmlDoc.write('body.loaded{background:#111;justify-content:flex-start}')
      htmlDoc.write('</style>')
      htmlDoc.write('</head>')
      htmlDoc.write('<body>')
      htmlDoc.write('<div id="loader" class="card loader"><span class="spin"></span>Generando vista previa del PDF...</div>')
      htmlDoc.write('<iframe id="pdfFrame" src="' + urlWithAuth + '" title="' + fileName + '" name="' + fileName + '"></iframe>')
      htmlDoc.write('<script>')
      htmlDoc.write('var loader = document.getElementById("loader");')
      htmlDoc.write('var frame = document.getElementById("pdfFrame");')
      htmlDoc.write('frame.onload = function() {')
      htmlDoc.write('  setTimeout(function() {')
      htmlDoc.write('    loader.classList.add("hidden");')
      htmlDoc.write('    frame.classList.add("loaded");')
      htmlDoc.write('    document.body.classList.add("loaded");')
      htmlDoc.write('  }, 500);')
      htmlDoc.write('};')
      htmlDoc.write('frame.onerror = function() {')
      htmlDoc.write('  var errorSpan = document.createElement("span");')
      htmlDoc.write('  errorSpan.textContent = "Error al cargar el PDF";')
      htmlDoc.write('  loader.innerHTML = "";')
      htmlDoc.write('  loader.appendChild(errorSpan);')
      htmlDoc.write('};')
      htmlDoc.write('</' + 'script>')
      htmlDoc.write('</body>')
      htmlDoc.write('</html>')
      htmlDoc.close()
    } else {
      // Fallback: descargar directamente
      const service2 = new ReportService()
      const blob = await service2.downloadReportPDF(reportStore.currentReport.id)
      const url = window.URL.createObjectURL(blob)
      const tmp = document.createElement('a')
      tmp.href = url
      tmp.download = fileName
      document.body.appendChild(tmp)
      tmp.click()
      tmp.remove()
      setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    }
  } catch (error) {
    console.error('Error previewing PDF:', error)
    toast.error('No se pudo abrir la vista previa')
  }
  finally {
    isPreviewing.value = false
  }
}

// Helper functions
const getStatusColor = (status: string) => {
  const colors = {
    excellent: 'border-green-500 bg-green-50',
    good: 'border-blue-500 bg-blue-50',
    fair: 'border-yellow-500 bg-yellow-50',
    poor: 'border-red-500 bg-red-50'
  }
  return colors[status as keyof typeof colors] || 'border-gray-500 bg-gray-50'
}

const getStatusLabel = (status: string) => {
  const labels = {
    excellent: 'Excelente',
    good: 'Bueno',
    fair: 'Aceptable',
    poor: 'Deficiente'
  }
  return labels[status as keyof typeof labels] || status
}

const getSeverityBorder = (severity: string) => {
  const borders = {
    alta: 'border-red-500',
    media: 'border-yellow-500',
    baja: 'border-green-500'
  }
  return borders[severity as keyof typeof borders] || 'border-gray-500'
}

const getSeverityBadge = (severity: string) => {
  const badges = {
    alta: 'bg-red-100 text-red-800',
    media: 'bg-yellow-100 text-yellow-800',
    baja: 'bg-green-100 text-green-800'
  }
  return badges[severity as keyof typeof badges] || 'bg-gray-100 text-gray-800'
}

const getPriorityBadge = (priority: string) => {
  const badges = {
    urgente: 'bg-red-100 text-red-800',
    alta: 'bg-orange-100 text-orange-800',
    media: 'bg-yellow-100 text-yellow-800',
    baja: 'bg-green-100 text-green-800'
  }
  return badges[priority as keyof typeof badges] || 'bg-gray-100 text-gray-800'
}

const getScoreColor = (score: number) => {
  if (score >= 90) return 'text-green-600'
  if (score >= 70) return 'text-blue-600'
  if (score >= 50) return 'text-yellow-600'
  return 'text-red-600'
}
</script>
